import { useEffect, useRef } from "react";
import { getExtensionFile } from "../utils/string";
import {
  PENDING_GALLERY_CREATIVES_KEY,
  filterMediaByAcceptedFormats,
  getAcceptedFormatsKey,
  getGalleryHydrationKey,
  mediaListsMatch,
  mergeUniqueMediaUrls,
} from "./galleryCreativeMedia";

export {
  PENDING_GALLERY_CREATIVES_KEY,
  filterMediaByAcceptedFormats,
  getAcceptedFormatsKey,
  getGalleryHydrationKey,
  getGalleryMediaIdentity,
  mergeUniqueMediaUrls,
} from "./galleryCreativeMedia";

const filterGalleryMedia = (mediaUrls, acceptedFormats) =>
  filterMediaByAcceptedFormats(mediaUrls, acceptedFormats, getExtensionFile);

export const useHydratePendingGalleryCreatives = ({
  initialData,
  isLoadingDefaults,
  currentAnswer,
  creativeStepName,
  fieldName,
  platformKey,
  galleryHydrationRef,
  setValue,
  getValues,
  acceptedFormats = [],
}) => {
  const internalHydrationRef = useRef({ hydratedPlatforms: new Set() });
  const hydrationRef = galleryHydrationRef ?? internalHydrationRef;
  const acceptedFormatsRef = useRef(acceptedFormats);
  acceptedFormatsRef.current = acceptedFormats;
  const formatsKey = getAcceptedFormatsKey(acceptedFormats);

  useEffect(() => {
    if (isLoadingDefaults || !platformKey || !hydrationRef?.current) {
      return;
    }

    const pendingCreatives = initialData?.[PENDING_GALLERY_CREATIVES_KEY];
    if (!Array.isArray(pendingCreatives) || pendingCreatives.length === 0) {
      return;
    }

    if (currentAnswer !== creativeStepName) {
      return;
    }

    const formats = acceptedFormatsRef.current || [];
    const hydrationKey = getGalleryHydrationKey(platformKey, formats);
    const hydratedPlatforms = hydrationRef.current.hydratedPlatforms;
    if (hydratedPlatforms.has(hydrationKey)) {
      return;
    }

    const platformPrefix = `${platformKey}:`;
    const hadPreviousAttempt = [...hydratedPlatforms].some((key) =>
      key.startsWith(platformPrefix)
    );
    const filteredCreatives = filterGalleryMedia(pendingCreatives, formats);

    hydratedPlatforms.add(hydrationKey);

    const currentValues = getValues(fieldName) || [];
    const baseValues = hadPreviousAttempt
      ? filterGalleryMedia(currentValues, formats)
      : currentValues;
    const mergedValues = mergeUniqueMediaUrls(baseValues, filteredCreatives);

    if (!mediaListsMatch(currentValues, mergedValues)) {
      setValue(fieldName, mergedValues);
    }
  }, [
    creativeStepName,
    currentAnswer,
    fieldName,
    formatsKey,
    hydrationRef,
    getValues,
    initialData,
    isLoadingDefaults,
    platformKey,
    setValue,
  ]);
};
